import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  GridItem,
  VStack,
  Button,
  Text,
  useToast,
  Skeleton,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import DynamicFormContainer from "components/CommonForm/DynamicFormContainer";
import ProfileCard from "components/CommonForm/ProfileCard";
import { productsApiAction } from "stores/redux/apiSlices/productApi/productApiSlice";
import { useDispatch } from "react-redux";
import productLabels from "variables/greek.product.json";

const AddProduct = ({ productData, onaddProductStatus }) => {
  const dispatch = useDispatch();
  const toast = useToast();

  const [formData, setFormData] = useState({
    productName: productData?.productName || "",
    unitOfMeasure: productData?.unitOfMeasure || "",
    price: productData?.price || "",
    description: productData?.description || "",
    quantityTypes: productData?.quantityTypes || "",
    quantity: productData?.allowedQuantities || "",
  });
  const isEdit = !!productData?.productId;
  const [addProducts] = productsApiAction.addProductApi();
  const [updateProducts] = productsApiAction.updateProductApi();
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [status, setStatus] = useState(productData?.status || false);

  const {
    data,
    error,
    isLoading,
    refetch,
  } = productsApiAction.getProductsMasterListApi({
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const defaultQuantities = data?.data?.defaultQuantities || [];
  const quantityTypes = data?.data?.quantityTypes || [];
  const unitTypes = data?.data?.unitTypes || [];

  const unitTypeOptions = unitTypes.map((val) => ({
    label: productLabels.productForm.unitOptions[val] || val,
    value: val,
  }));

  const quantityTypeOptions = quantityTypes.map((val) => ({
    label: productLabels.productForm.quantityTypeOptions[val] || val,
    value: val,
  }));

  const defaultQuantityOptions = defaultQuantities.map((val) => ({
    label: val,
    value: val,
  }));

  useEffect(() => {
    if (!formData.quantityTypes) return;

    if (!productData) {
      if (formData.quantityTypes === "FIXED" && defaultQuantities.length) {
        const quantitiesString = defaultQuantities.join(", ");
        setFormData((prev) => ({
          ...prev,
          quantity: quantitiesString,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          quantity: "",
        }));
      }
    }
  }, [formData.quantityTypes, data, productData]);

  const handleToggle = () => {
    setStatus((prev) => !prev);
  };

  useEffect(() => {
    if (productData) {
      setFormData((prevData) => ({
        ...prevData,
        id: productData.productId || "",
        productName: productData.name || prevData.productName,
        unitOfMeasure: productData.measurementType || prevData.unitOfMeasure,
        price: productData.price
          ? parseFloat(productData.price)
          : prevData.price,
        description: productData.description || prevData.description,
        quantityTypes: productData.quantityType || "",
        quantity: productData.allowedQuantities || "",
      }));
    } else if (data?.data?.defaultQuantities) {
      setFormData((prevData) => ({
        ...prevData,
        quantity: data.data.defaultQuantities.join(", "),
      }));
    }
  }, [productData, data]);

  const validateForm = () => {
    let newErrors = {};
    formConfig[0].fields.forEach((field) => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = "Αυτό το πεδίο είναι υποχρεωτικό";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    const payload = {
      name: formData.productName,
      description: formData.description,
      price: Number(formData.price),
      measurementType: formData.unitOfMeasure,
      quantityType: formData.quantityTypes,
      ...(formData.quantityTypes !== "FLEXIBLE" && {
        allowedQuantities: formData.quantity
          .split(",")
          .map((q) => q.trim())
          .filter((q) => q !== "")
          .join(","),
      }),
    };

    const action = formData?.id
      ? updateProducts({ productId: formData.id, status: status, ...payload })
      : addProducts(payload);

    // simulate delay
    setTimeout(() => {
      action
        .unwrap()
        .then((response) => {
          toast({
            title: "Success",
            description: response.message || "Οδηγός δημιουργήθηκε με επιτυχία",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          onaddProductStatus?.(response.success);
        })
        .catch((error) => {
          toast({
            title: "Error",
            description: error.data?.message || "Αποτυχία δημιουργίας οδηγού",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        })
        .finally(() => setIsSubmitting(false));
    }, 1000);
  };

  useEffect(() => {
    if (formData.quantityTypes === "FLEXIBLE") {
      setFormData((prev) => ({
        ...prev,
        quantity: "",
      }));
    }
  }, [formData.quantityTypes]);

  const baseFields = [
    {
      name: "productName",
      label: productLabels.productForm.productName.label,
      placeholder: productLabels.productForm.productName.placeholder,
      type: "text",
      required: true,
      gridSize: 6,
    },
    {
      name: "unitOfMeasure",
      label: productLabels.productForm.unitOfMeasure.label,
      placeholder: productLabels.productForm.unitOfMeasure.placeholder,
      type: "select",
      options: unitTypeOptions,
      required: true,
      gridSize: 3,
      searchable: false,
    },
    {
      name: "quantityTypes",
      label: productLabels.productForm.quantityTypes.label,
      placeholder: productLabels.productForm.quantityTypes.placeholder,
      type: "select",
      options: quantityTypeOptions,
      required: true,
      gridSize: 3,
      searchable: false,
    },
  ];

  if (formData.quantityTypes && formData.quantityTypes !== "FLEXIBLE") {
    baseFields.push({
      name: "quantity",
      label: productLabels.productForm.quantity.label,
      placeholder: productLabels.productForm.quantity.placeholder,
      type: "text",
      required: true,
      gridSize: 6,
    });
  }

  baseFields.push(
    {
      name: "price",
      label: productLabels.productForm.price.label,
      placeholder: productLabels.productForm.price.placeholder,
      type: "number",
      required: true,
      gridSize: 6,
    },
    {
      name: "description",
      label: productLabels.productForm.description.label,
      placeholder: productLabels.productForm.description.placeholder,
      type: "text",
      required: false,
      gridSize: 12,
    }
  );

  const formConfig = [
    {
      id: "productInfo",
      title: "Πληροφορίες Προϊόντος",
      fields: baseFields,
    },
  ];

  return (
    <Box mt={24}>
      <Skeleton isLoaded={!isLoading} borderRadius="md">
        {isEdit && (
          <ProfileCard
            name={formData.productName}
            email={formData.email}
            profileImage={formData.clientImage || ""}
            isActive={status}
            onToggle={() => handleToggle()}
          />
        )}
      </Skeleton>

      <Grid mt={4} templateColumns="1fr" gap={6} alignItems="start">
        <GridItem>
          <VStack spacing={6} align="stretch">
            <Skeleton isLoaded={!isLoading} borderRadius="md">
              <DynamicFormContainer
                key={formConfig[0].id}
                title={formConfig[0].title}
                cardData={formConfig[0]}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
              />
            </Skeleton>
          </VStack>
        </GridItem>
      </Grid>

      {Object.keys(errors).length > 0 && (
        <Text color="red.500" mt={4}>
          Παρακαλώ διορθώστε τα σφάλματα πριν την υποβολή.
        </Text>
      )}

      <Skeleton isLoaded={!isLoading} mt={6} w="fit-content" borderRadius="md">
        <Button
          mt={6}
          variant="no-hover"
          bg="linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)"
          minW="125px"
          h="35px"
          color="#fff"
          fontSize="xs"
          fontWeight="bold"
          alignSelf={{ sm: "flex-start", lg: null }}
          mb={{ sm: "12px", md: "0px" }}
          onClick={handleSubmit}
          isDisabled={isSubmitting}
        >
          {isSubmitting ? (
            <Spinner size="sm" color="white" />
          ) : isEdit ? (
            "Ενημέρωση Προϊόντος"
          ) : (
            "Προσθήκη Προϊόντος"
          )}
        </Button>
      </Skeleton>
    </Box>
  );
};

export default AddProduct;
